import Typography from '@mui/material/Typography';
import {
  LpFeeCalculationBasis,
  LpFeeCommitmentStatus,
} from 'further-types/lp/lp-fee-template';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { Column } from 'further-ui/components/Layout';
import TooltipTypography from 'further-ui/components/TooltipTypography';
import {
  LpFeeCalculationBasisLabels,
  LpFeeCommitmentStatusLabels,
} from 'further-ui/labels';
import {
  CalendarDay,
  CalendarTime,
  numberToCurrencyString,
} from 'further-ui/utils';
import { round } from 'lodash';

type FeeDetailsForCapitalAction = {
  _id: string;
  feeActionId: string;
  limitedPartner: string;
  feeActionCreatedAt: CalendarTime;
  accrualDate: CalendarDay;
  lpFeeTemplateName: string;
  lpFeeTemplateFeeName: string;
  insideOutsideCommitment: LpFeeCommitmentStatus;
  calculationBasis: LpFeeCalculationBasis;
  feeExTaxLcy: number;
  salesTaxAmountLcy: number;
  feeIncTaxLcy: number;
};

type FeeRow = FeeDetailsForCapitalAction & { id: string };

type Props = {
  fees: Array<FeeDetailsForCapitalAction>;
};

const columns: Array<TableColumn<FeeRow>> = [
  {
    label: 'Limited partner',
    key: 'limitedPartner',
    width: 200,
  },
  {
    label: 'Accrual date',
    key: 'accrualDate',
    width: 140,
    render: (fee) => fee.accrualDate.toReadable(),
  },
  {
    label: 'Fee template name',
    key: 'lpFeeTemplateName',
    width: 200,
    render: (fee) => (
      <TooltipTypography
        variant="inherit"
        tooltip={
          <Column spacing="xxs">
            <Typography variant="caption" color="inherit">
              <b>Fee name:</b> {fee.lpFeeTemplateFeeName}
            </Typography>
            <Typography variant="caption" color="inherit">
              <b>Inside/outside commitment:</b>{' '}
              {LpFeeCommitmentStatusLabels.get(fee.insideOutsideCommitment)}
            </Typography>
            <Typography variant="caption" color="inherit">
              <b>Calculation basis:</b>{' '}
              {LpFeeCalculationBasisLabels.get(fee.calculationBasis)}
            </Typography>
          </Column>
        }
      >
        {fee.lpFeeTemplateName}
      </TooltipTypography>
    ),
  },
  {
    label: 'Fee',
    key: 'feeExTaxLcy',
    width: 140,
    render: (fee) => numberToCurrencyString(fee.feeExTaxLcy),
  },
  {
    label: 'Sales tax',
    key: 'salesTaxAmountLcy',
    width: 140,
    render: (fee) => numberToCurrencyString(fee.salesTaxAmountLcy),
  },
  {
    label: 'Total charge',
    key: 'feeIncTaxLcy',
    width: 140,
    render: (fee) =>
      numberToCurrencyString(round(fee.feeExTaxLcy + fee.salesTaxAmountLcy, 2)),
  },
];

const FeesPreviewTable: React.FC<Props> = ({ fees }) => {
  const rows: Array<FeeRow> = fees.map((fee) => ({ ...fee, id: fee._id }));

  return (
    <Table
      scrollable
      showTopBorder
      showHeaderBorder
      rows={rows}
      columns={columns}
    />
  );
};

export default FeesPreviewTable;
